import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { COUNTRY_CODES } from '../../constants/countryCodes';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
const TRAINER_TYPES = {
  'Group Fitness Instructor': 'Specializes in leading classes (e.g., spinning, yoga, boot camps, HIIT).',
  'Health Coach': 'Focuses on a holistic approach to help clients make behavioral and lifestyle changes (e.g., diet, stress management, chronic disease prevention).',
  'Corrective Exercise Specialist': 'Assesses and addresses muscular imbalances and movement deficiencies to help prevent injuries.',
  'Youth Fitness Specialist': 'Works specifically with children and adolescents.',
  'Senior Fitness Specialist': 'Designs programs for older adults, focusing on maintaining mobility, strength, and balance.',
  'Weight Loss Specialist': 'Develops comprehensive programs centered on weight management and body transformation.',
  'Physique/Bodybuilding Trainer': 'Specializes in training for aesthetic goals or competitive bodybuilding.',
  'Medical Exercise Specialist': 'Works with clients who have chronic health conditions or are in post-rehabilitation phases, often in a clinical setting.',
  'Strength & Conditioning Coach': 'Works with athletes to optimize performance; the NSCA Certified Strength and Conditioning Specialist (CSCS) is often a requirement for collegiate and professional sports.'
};

const TRAINER_CERTIFICATIONS = {
  'ACE': 'American Council on Exercise: Known for its holistic approach, offering certifications for personal training, group fitness, health coaching, and medical exercise specialization.',
  'NASM': 'National Academy of Sports Medicine: Highly respected for its Optimum Performance Training (OPT) model and strong focus on corrective exercise and injury prevention.',
  'ACSM': 'American College of Sports Medicine: Considered the scientific standard, ideal for those working in clinical settings or with special populations due to its rigorous, evidence-based approach.',
  'NSCA': 'National Strength and Conditioning Association: Offers a certified personal trainer (CPT) certification and the prestigious Certified Strength and Conditioning Specialist (CSCS) for professionals training athletes.',
  'ISSA': 'International Sports Sciences Association: Known for flexibility with online study options and a variety of specializations, including a nutrition certification option.',
  'NCSF': 'National Council on Strength & Fitness: Provides an NCCA-accredited CPT known for its strong focus on exercise prescription and programming.'
};

function TrainerProviderForm({ onClose, onSave, fetchWithAuth, editingTrainer }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [formData, setFormData] = useState({
    trainer_name: '',
    company_association: '',
    trainer_type: '',
    trainer_certification: '',
    trainer_address: '',
    phone_country_code: '+1',
    trainer_phone: '',
    trainer_email: '',
    trainer_description: ''
  });

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (editingTrainer) {
      setFormData({
        trainer_name: editingTrainer.trainer_name || '',
        company_association: editingTrainer.company_association || '',
        trainer_type: editingTrainer.trainer_type || '',
        trainer_certification: editingTrainer.trainer_certification || '',
        trainer_address: editingTrainer.trainer_address || '',
        phone_country_code: editingTrainer.phone_country_code || '+1',
        trainer_phone: editingTrainer.trainer_phone || '',
        trainer_email: editingTrainer.trainer_email || '',
        trainer_description: editingTrainer.trainer_description || ''
      });
    }
  }, [editingTrainer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    try {
      const url = editingTrainer
        ? `${API_BASE}/api/trainers/${editingTrainer.id}`
        : `${API_BASE}/api/trainers`;

      const method = editingTrainer ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
      } else {
        const errorData = await response.json();
        if (response.status === 409) {
          setErrorMessage('A trainer with this name and email already exists.');
        } else {
          setErrorMessage(errorData.detail || 'Failed to save trainer');
        }
      }
    } catch (error) {
      console.error('Failed to save trainer:', error);
      setErrorMessage('Failed to save trainer. Please try again.');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-content-md">
        <div className="modal-header">
          <h3>{editingTrainer ? 'Edit Trainer / Provider' : 'Add Trainer / Provider'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {errorMessage && (
          <div className="tpf-errorMessage">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Trainer Name - REQUIRED */}
          <div className="form-group">
            <label className="form-label">Trainer Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.trainer_name}
              onChange={(e) => setFormData(prev => ({...prev, trainer_name: e.target.value}))}
              placeholder="e.g., Mike Johnson, CPT"
              required
            />
          </div>

          {/* Company / Gym Association */}
          <div className="form-group">
            <label className="form-label">Company / Gym Association</label>
            <input
              type="text"
              className="form-input"
              value={formData.company_association}
              onChange={(e) => setFormData(prev => ({...prev, company_association: e.target.value}))}
              placeholder="e.g., Gold's Gym, Equinox, Independent"
            />
          </div>

          {/* Trainer Type - REQUIRED with tooltips */}
          <div className="form-group">
            <label className="form-label">Trainer Type *</label>
            <select
              className="form-input"
              value={formData.trainer_type}
              onChange={(e) => setFormData(prev => ({...prev, trainer_type: e.target.value}))}
              required
            >
              <option value="">Select Trainer Type... hover over options for definition</option>
              {Object.keys(TRAINER_TYPES).map(type => (
                <option
                  key={type}
                  value={type}
                  title={TRAINER_TYPES[type]}
                >
                  {type}
                </option>
              ))}
            </select>
            <div className="tpf-hintText">
              Hover over each option to see its definition
            </div>
          </div>

          {/* Trainer Certification - REQUIRED with tooltips */}
          <div className="form-group">
            <label className="form-label">Trainer Certification *</label>
            <select
              className="form-input"
              value={formData.trainer_certification}
              onChange={(e) => setFormData(prev => ({...prev, trainer_certification: e.target.value}))}
              required
            >
              <option value="">Select Certification... hover over options for definition</option>
              {Object.keys(TRAINER_CERTIFICATIONS).map(cert => (
                <option
                  key={cert}
                  value={cert}
                  title={TRAINER_CERTIFICATIONS[cert]}
                >
                  {cert}
                </option>
              ))}
            </select>
            <div className="tpf-hintText">
              Hover over each option to see its definition
            </div>
          </div>

          {/* Trainer Address */}
          <div className="form-group">
            <label className="form-label">Trainer Address</label>
            <textarea
              className="form-textarea"
              value={formData.trainer_address}
              onChange={(e) => setFormData(prev => ({...prev, trainer_address: e.target.value}))}
              placeholder="Gym/Office Address"
              rows="3"
            />
          </div>

          {/* Trainer Phone with Country Code */}
          <div className="form-group">
            <label className="form-label">Trainer Phone</label>
            <div className="flex gap-3">
              <select
                className="form-input tpf-countryCode"
                value={formData.phone_country_code}
                onChange={(e) => setFormData(prev => ({...prev, phone_country_code: e.target.value}))}

              >
                {COUNTRY_CODES.map(({ code, country }) => (
                  <option key={code} value={code}>
                    {code} ({country})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                className="form-input flex-1"
                value={formData.trainer_phone}
                onChange={(e) => setFormData(prev => ({...prev, trainer_phone: e.target.value}))}
                placeholder="Phone number"
              />
            </div>
          </div>

          {/* Trainer Email - REQUIRED */}
          <div className="form-group">
            <label className="form-label">Trainer Email *</label>
            <input
              type="email"
              className="form-input"
              value={formData.trainer_email}
              onChange={(e) => setFormData(prev => ({...prev, trainer_email: e.target.value}))}
              placeholder="trainer@example.com"
              required
            />
          </div>

          {/* Trainer Description */}
          <div className="form-group">
            <label className="form-label">Notes / Description</label>
            <textarea
              className="form-textarea"
              value={formData.trainer_description}
              onChange={(e) => setFormData(prev => ({...prev, trainer_description: e.target.value}))}
              placeholder="Additional notes about this trainer..."
              rows="4"
            />
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingTrainer ? 'Update Trainer' : 'Save Trainer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TrainerProviderForm;
